import React, { FC, useMemo } from "react";
import { Redirect, Route, RouteComponentProps, RouteProps, useLocation } from "react-router-dom";

import { Credentials, UserProfile } from "auth/modules/state";

export interface ProtectedRouteProps extends RouteProps {
    profile: UserProfile;
    credentials: Credentials;
    role?: RouteRole;
    ignoreVerified?: boolean;
}

export type RouteRole = "xappAdmin" | "stentor_admin" | "USER";

/**
 * Based on this example https://reacttraining.com/react-router/web/example/auth-workflow
 */
export const ProtectedRoute: FC<ProtectedRouteProps> = (props: ProtectedRouteProps) => {

    const requiredRole = props.role ? props.role : "USER";
    const authorized = isAuthorized(props.profile, requiredRole);
    const currentPath = useLocation().pathname;
    const search = useLocation().search;
    // Memoize the component to avoid unnecessary re-renders
    const Component = useMemo(() => props.component, [props.component]);

    // Redirect if not authorized
    if (!authorized && currentPath !== "/login") {
        return (
            <Redirect to={{
                pathname: "/login",
                state: {
                    from: {
                        pathname: currentPath,
                        search: search
                    }
                }
            }} />
        );
    }

    return <Route {...props} render={(componentProps) => <Component {...componentProps} />} />;
};


/**
 * Generate a renderer for the protected route.  It will decide what gets displayed.
 *
 * If the user is not logged in, it will navigate the user to the login page.
 * If the user is logged in but not verified, it will navigate the user to the verify page.
 * If the user is logged in and verified, it will show the components that the route contains.
 * @param routeProps
 */
export function generateRenderer(routeProps: ProtectedRouteProps): FC<RouteComponentProps<any>> {
    const requiredRole = routeProps.role ? routeProps.role : "USER";
    // const isVerified = routeProps.profile ? !!routeProps.profile.emailVerified : false;
    // const requireVerified = !routeProps.ignoreVerified;
    const authorized = isAuthorized(routeProps.profile, requiredRole);

    return (componentProps) => {
        // If we're already on the path then there's no reason to navigate to it.
        const currentPath = componentProps.location.pathname;
        if (!authorized && currentPath !== "/login") {
            return (
                <Redirect to={{
                    pathname: "/login",
                    state: {
                        from: {
                            pathname: componentProps.location.pathname,
                            search: componentProps.location.search
                        }
                    }
                }} />
            );
        }

        /*
         * Amazon accounts are being flagged as unverified with no immediately obvious way to verify them
         * disabling until further notice
         */
        // if (!isVerified && currentPath !== "/login" && currentPath !== "/verify" && requireVerified) {
        //     return (<Redirect to={{
        //         pathname: "/verify",
        //         state: { from: componentProps.location }
        //     }} />);
        // }

        return (<routeProps.component {...componentProps} />);
    };
}

/**
 * Function that determines if the user is authorized to access the page.
 *
 * This does not check *verification* which is a different setup.
 *
 * @export
 * @param {Auth0UserProfile} profile
 * @param {string} requiredRole
 * @returns
 */
export function isAuthorized(profile: UserProfile, requiredRole: RouteRole): boolean {
    if (profile) {
        if (requiredRole === "USER") {
            return true;
        }
        if (requiredRole === "xappAdmin" || requiredRole === "stentor_admin") {
            return profile.isAdmin;
        }
    }
    return false;
}
